import type { DataSourceIdentity } from '@lighthouse/core'
import type { MarkdownImportFileResult, MarkdownImportParams, MarkdownImportSummary } from '@lighthouse/shared'
import { markdownToTiptapJson } from '@lighthouse/shared'
import { useCallback } from 'react'

import * as srv from '@/services'

const PAGE_SIZE = 200

interface UseMarkdownImportParams {
    appId: string
    envId: string
    dsId: string
}

const normalizeMatchKey = (value: unknown): string => {
    if (value === null || value === undefined) {
        return ''
    }
    return String(value).trim().toLowerCase()
}

/**
 * 分页拉取数据表全部记录，建立「映射字段值 -> 记录 id」索引。
 * 不依赖后端 filter 语义，避免复杂的 FilterFormType ADT。
 */
const buildMatchIndex = async ({ appId, envId, dsId, matchFieldId }: DataSourceIdentity & { matchFieldId: string }) => {
    const index = new Map<string, string>()
    let currentPage = 1

    while (true) {
        const result = await srv.getDs({ appId, envId, dsId, pagination: { currentPage, pageSize: PAGE_SIZE } })
        const records = result?.records ?? []
        for (const record of records) {
            const matchValue = record.content?.[matchFieldId]?.value
            const key = normalizeMatchKey(matchValue)
            if (!key || !record.id) {
                continue
            }
            // 同名记录取最后一条（v1 行为）
            index.set(key, record.id)
        }
        if (records.length < PAGE_SIZE) {
            break
        }
        currentPage += 1
    }
    return index
}

export const useMarkdownImport = ({ appId, envId, dsId }: UseMarkdownImportParams) => {
    const importMarkdown = useCallback(
        async ({ files, noteFieldId, matchFieldId }: MarkdownImportParams): Promise<MarkdownImportSummary> => {
            const index = await buildMatchIndex({ appId, envId, dsId, matchFieldId })

            const details: MarkdownImportFileResult[] = []
            let success = 0
            let fail = 0
            let skip = 0

            for (const file of files) {
                const recordId = index.get(normalizeMatchKey(file.name))
                if (!recordId) {
                    skip += 1
                    details.push({ fileName: file.fileName, status: 'skip', message: '未找到匹配记录' })
                    continue
                }
                try {
                    const json = markdownToTiptapJson(file.content)
                    await srv.updateCell({
                        envId,
                        dsId,
                        id: recordId,
                        fieldId: noteFieldId,
                        content: { value: json }
                    })
                    success += 1
                    details.push({ fileName: file.fileName, status: 'success' })
                } catch (error) {
                    fail += 1
                    details.push({
                        fileName: file.fileName,
                        status: 'fail',
                        message: error instanceof Error ? error.message : '写入失败'
                    })
                }
            }

            return { success, fail, skip, details }
        },
        [appId, dsId, envId]
    )

    return importMarkdown
}
